import { skipToken, useSuspenseQuery } from '@apollo/client/react'
import { Navigate, Outlet, useLocation, useParams } from '@faceup/router'
import { usePromiseSuspense } from '@faceup/ui'
import { Suspense } from 'react'
import { graphql } from '../../../__generated__'
import { FullScreenLoader } from '../../../Components/FullScreenLoader'
import { SurveyLayout } from '../../../Components/SurveyLayout/SurveyLayout'
import { UnavailableLink } from '../../../Components/UnavailableLink'
import { getLanguageRedirect } from '../../../utils/languageRedirect'
import { shouldSurveyShortLinkRedirectToReportingForm } from '../../../utils/shortId'
import { useRegion } from '../../../utils/useRegion'
import { LanguageWrapper } from '../../LanguageWrapper'
import { SurveyProvider } from '../../SurveyProvider'
import { UiProvider } from '../../UiProvider'

const query = graphql(`
  query SurveyInitByShortIdQuery($shortId: GraphQLString!) {
    publicSurveyByShortId(shortId: $shortId) {
      id
      defaultLanguage
      institution {
        id
        type
        isTesting
        billing {
          id
          plan
        }
        config {
          id
          institutionName
          primaryColor
        }
        partner {
          id
          config {
            id
            primaryColor
          }
        }
      }
      ...SurveyLayout_survey
      ...SurveyProvider_survey
    }
  }
`)

/**
 * Element of the survey route branch (q/:shortId). Discovers the region, runs
 * the survey init query (app shell: layout + provider slices), forces the
 * survey's language, then hands the shell data top-down. Survey pages run their
 * own content queries below the Outlet.
 *
 * Web flow for now; the app client is wired in the mobile-isolation phase.
 */
export const SurveyWrapper = () => {
  const { shortId } = useParams<'shortId'>()
  const location = useLocation()
  const { discoverByShortlink } = useRegion()

  // Sets the module-level region before the regional query runs.
  const discovery = usePromiseSuspense(discoverByShortlink, [shortId ?? ''])

  // The redirected short link resolves in discovery but is a reporting form, so the survey query
  // would only throw its way to the error boundary before the redirect below.
  const redirectToReportingForm = shouldSurveyShortLinkRedirectToReportingForm(shortId)

  // Discovery has already said an unknown short link resolves to nothing, so the
  // query is skipped rather than fired at the default region just to fail.
  const { data } = useSuspenseQuery(
    query,
    discovery && !redirectToReportingForm ? { variables: { shortId: shortId ?? '' } } : skipToken
  )

  if (redirectToReportingForm) {
    return <Navigate to={routes => routes.page({ shortId })} replace />
  }

  const survey = data?.publicSurveyByShortId
  if (!discovery || !survey) {
    return <UnavailableLink shortId={shortId} />
  }

  // Surveys are mono-language: always the survey's default language.
  const effectiveLanguage = survey.defaultLanguage
  const languageRedirect = getLanguageRedirect(location, effectiveLanguage)
  if (languageRedirect) {
    return <Navigate to={() => languageRedirect} replace />
  }

  return (
    <LanguageWrapper language={effectiveLanguage}>
      <UiProvider
        primaryColor={
          survey.institution.config?.primaryColor ??
          survey.institution.partner?.config?.primaryColor ??
          undefined
        }
        institutionName={survey.institution.config?.institutionName ?? ''}
        mother={{
          type: survey.institution.type ?? null,
          isTesting: survey.institution.isTesting ?? null,
          plan: survey.institution.billing?.plan ?? null,
        }}
      >
        <SurveyProvider survey={survey}>
          <SurveyLayout survey={survey}>
            <Suspense fallback={<FullScreenLoader />}>
              <Outlet />
            </Suspense>
          </SurveyLayout>
        </SurveyProvider>
      </UiProvider>
    </LanguageWrapper>
  )
}
